/* ─────────────────────────────────────────────────────────────
   The Standard tab's hex grid.

   A hexagon of pointy-top hexagonal cells in axial coordinates,
   coloured by polar position: angle picks the hue, distance from
   the centre runs pale (near-white middle) through vivid to dark
   at the rim, so the grid spans light, saturated and dark without
   a second control. Deliberately an approximation of Office's
   swatch set rather than a copy of it.

   Pure geometry — no DOM, no React.
─────────────────────────────────────────────────────────────── */

import { hslToRgb, toHex } from './colorMath';

/**
 * Hexagon of cells as [{ q, r, x, y, hex }], with x/y in pixels
 * relative to the centre. `size` is centre-to-vertex.
 */
export function buildHoneycomb({ rings = 5, size = 12 } = {}) {
  const cells = [];
  for (let q = -rings; q <= rings; q += 1) {
    const rLo = Math.max(-rings, -q - rings);
    const rHi = Math.min(rings, -q + rings);
    for (let r = rLo; r <= rHi; r += 1) {
      cells.push({ q, r, x: size * 1.5 * q, y: size * Math.sqrt(3) * (r + q / 2) });
    }
  }

  const maxDist = cells.reduce((m, c) => Math.max(m, Math.hypot(c.x, c.y)), 0) || 1;

  return cells.map(cell => {
    const dist = Math.hypot(cell.x, cell.y);
    const t = dist / maxDist;                       // 0 centre → 1 rim
    const angle = (Math.atan2(cell.y, cell.x) * 180) / Math.PI;
    const h = (angle + 360) % 360;
    const s = Math.min(1, t * 1.3);                 // grey centre, saturated by mid
    const l = 0.92 - 0.64 * t;                      // near-white centre → dark rim
    return { ...cell, hex: toHex(hslToRgb({ h, s, l })) };
  });
}

/** Neutral strip that sits under the hexagon: white through black. */
export function buildGrayscale(steps = 12) {
  return Array.from({ length: steps }, (_, i) => {
    const l = 1 - i / (steps - 1);
    return { hex: toHex(hslToRgb({ h: 0, s: 0, l })) };
  });
}

/** Bounding box of a built honeycomb, for sizing its container. */
export function honeycombBounds(cells, size) {
  const xs = cells.map(c => c.x);
  const ys = cells.map(c => c.y);
  return {
    width: Math.max(...xs) - Math.min(...xs) + size * 2,
    height: Math.max(...ys) - Math.min(...ys) + size * Math.sqrt(3),
    minX: Math.min(...xs) - size,
    minY: Math.min(...ys) - (size * Math.sqrt(3)) / 2,
  };
}
